import { useTranslation } from 'react-i18next'
import { getMediaUrl } from '@/utils/media'
import { motion } from 'framer-motion'
import {
  Award,
  Shield,
  Leaf,
  Download,
  FileText,
  ChevronRight,
  CheckCircle2,
  Microscope,
  Factory
} from 'lucide-react'
import { SEO } from '@/components/SEO'
import { Button } from '@/components/ui/Button'
import { LocalizedLink } from '@/components/LocalizedLink'

function CertificationCard({
  icon: Icon,
  title,
  description,
  color,
  delay
}: {
  icon: React.ElementType
  title: string
  description: string
  color: string
  delay: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -8, transition: { duration: 0.3 } }}
      style={{
        padding: '2rem',
        borderRadius: '1.5rem',
        backgroundColor: '#ffffff',
        boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
        border: '1px solid #e2e8f0',
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: color }} />
      <div
        style={{
          width: '60px',
          height: '60px',
          borderRadius: '1rem',
          backgroundColor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '1.5rem',
        }}
      >
        <Icon style={{ width: '28px', height: '28px', color: color }} />
      </div>
      <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.75rem' }}>{title}</h3>
      <p style={{ fontSize: '0.95rem', color: '#64748b', lineHeight: 1.6 }}>{description}</p>
    </motion.div>
  )
}

function DownloadCard({
  title,
  description,
  href,
  delay
}: {
  title: string
  description: string
  href: string
  delay: number
}) {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay }}
      whileHover={{ scale: 1.02 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1.5rem',
        borderRadius: '1rem',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        textDecoration: 'none',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '0.75rem',
          backgroundColor: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <FileText style={{ width: '24px', height: '24px', color: '#64748b' }} />
      </div>
      <div style={{ flex: 1 }}>
        <h4 style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', marginBottom: '0.25rem' }}>{title}</h4>
        <p style={{ fontSize: '0.875rem', color: '#64748b' }}>{description}</p>
      </div>
      <Download style={{ width: '20px', height: '20px', color: '#00a3cc', flexShrink: 0 }} />
    </motion.a>
  )
}

export function CertificationsPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language === 'it' ? 'it' : 'en'

  const certifications = [
    {
      icon: Award,
      title: 'ISO 9001:2015',
      description: t('certifications.iso9001Desc'),
      color: '#003b7a',
    },
    {
      icon: Leaf,
      title: 'ISO 14001:2015',
      description: t('certifications.iso14001Desc'),
      color: '#10b981',
    },
    {
      icon: Shield,
      title: 'UNI PdR 125:2022',
      description: t('certifications.pdr125Desc'),
      color: '#8b5cf6',
    },
  ]

  const downloads = [
    { title: t('certifications.download.iso9001'), description: t('certifications.download.iso9001Desc'), href: getMediaUrl('documents/iso-9001-certificate.pdf') },
    { title: t('certifications.download.iso14001'), description: t('certifications.download.iso14001Desc'), href: getMediaUrl('documents/iso-14001-certificate.pdf') },
    { title: t('certifications.download.pdr125'), description: t('certifications.download.pdr125Desc'), href: getMediaUrl('documents/uni-pdr-125-policy.pdf') },
    { title: t('downloads.qualityPolicyTitle'), description: t('downloads.qualityPolicyDescription'), href: getMediaUrl(`documents/quality-environment-policy-${lang}.pdf`) },
    { title: t('certifications.download.tolerances'), description: t('certifications.download.tolerancesDesc'), href: getMediaUrl('documents/manufacturing-tolerances.pdf') },
    { title: t('certifications.download.ethicsCode'), description: t('certifications.download.ethicsCodeDesc'), href: getMediaUrl('documents/ethics-code.pdf') },
    { title: t('certifications.download.salesTerms'), description: t('certifications.download.salesTermsDesc'), href: getMediaUrl('documents/sales-terms-conditions.pdf') },
  ]

  const qualityPoints = [
    t('quality.commitment1', 'Rigorosa ispezione e test dei materiali in ingresso'),
    t('quality.commitment2', 'Controlli qualità in-process in ogni fase di produzione'),
    t('quality.commitment3', 'Test e certificazione del prodotto finale'),
    t('quality.commitment4', 'Miglioramento continuo attraverso il feedback dei clienti'),
  ]

  return (
    <>
      <SEO
        title={t('certifications.metaTitle')}
        description={t('certifications.metaDescription')}
      />

      {/* ============ HERO SECTION ============ */}
      <section
        style={{
          position: 'relative',
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #003b7a 0%, #001f42 50%, #00a3cc 100%)',
          marginTop: '-80px',
          paddingTop: '120px',
          paddingBottom: '80px',
          overflow: 'hidden',
        }}
      >
        {/* Decorative elements */}
        <div style={{
          position: 'absolute',
          top: '20%',
          right: '10%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(0,163,204,0.15) 0%, transparent 70%)',
          borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
          borderRadius: '50%',
        }} />

        <div className="container-main" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p style={{
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              color: '#00a3cc',
              marginBottom: '16px',
            }}>
              {t('certifications.pretitle')}
            </p>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: 700,
              color: '#ffffff',
              lineHeight: 1.1,
              marginBottom: '24px',
              maxWidth: '700px',
            }}>
              {t('certifications.heroTitle')}
            </h1>
            <p style={{
              fontSize: '18px',
              color: 'rgba(255,255,255,0.8)',
              maxWidth: '600px',
              lineHeight: 1.6,
            }}>
              {t('certifications.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============ CERTIFICATIONS SECTION ============ */}
      <section style={{ padding: '6rem 0', backgroundColor: '#f8fafc' }}>
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '3rem' }}
          >
            <span style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#00a3cc',
              textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}>
              {t('certifications.standardsLabel')}
            </span>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.5rem)',
              fontWeight: 800,
              color: '#0f172a',
              marginTop: '0.5rem'
            }}>
              {t('certifications.certificationsTitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {certifications.map((cert, index) => (
              <CertificationCard key={cert.title} {...cert} delay={index * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ QUALITY COMMITMENT SECTION ============ */}
      <section style={{ padding: '6rem 0', backgroundColor: '#ffffff' }}>
        <div className="container-main">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
            gap: '4rem',
            alignItems: 'center'
          }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#00a3cc',
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}>
                {t('certifications.commitmentLabel')}
              </span>
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                fontWeight: 800,
                color: '#0f172a',
                marginTop: '0.5rem',
                marginBottom: '1.5rem'
              }}>
                {t('certifications.qualityTitle')}
              </h2>
              <p style={{
                fontSize: '1.125rem',
                color: '#64748b',
                lineHeight: 1.7,
                marginBottom: '1.5rem'
              }}>
                {t('certifications.qualityText')}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem' }}>
                {qualityPoints.map((point, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <CheckCircle2 style={{ width: '20px', height: '20px', color: '#10b981', flexShrink: 0 }} />
                    <span style={{ color: '#334155', fontWeight: 500 }}>{point}</span>
                  </div>
                ))}
              </div>

              <LocalizedLink to="/contact">
                <Button variant="primary">
                  {t('buttons.contactUs')}
                  <ChevronRight style={{ marginLeft: '0.5rem', width: '18px', height: '18px' }} />
                </Button>
              </LocalizedLink>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              style={{ position: 'relative' }}
            >
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={getMediaUrl('about/manufacturing-process.jpeg')}
                  alt="Manufacturing process"
                  className="w-full aspect-[4/3] sm:aspect-[16/10] object-cover"
                />
              </div>

              {/* Floating badge - hidden on mobile */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="hidden sm:block absolute -bottom-4 sm:-bottom-5 -left-4 sm:-left-5 p-3 sm:p-6 rounded-lg sm:rounded-xl"
                style={{
                  backgroundColor: '#003b7a',
                  boxShadow: '0 20px 40px rgba(0,59,122,0.3)',
                }}
              >
                <Factory className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ DOWNLOADS SECTION ============ */}
      <section style={{ padding: '6rem 0', backgroundColor: '#f8fafc' }}>
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: '3rem' }}
          >
            <span style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#00a3cc',
              textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}>
              {t('certifications.resourcesLabel')}
            </span>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 2.5rem)',
              fontWeight: 800,
              color: '#0f172a',
              marginTop: '0.5rem'
            }}>
              {t('certifications.downloadsTitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 max-w-4xl mx-auto">
            {downloads.map((download, index) => (
              <DownloadCard key={download.title} {...download} delay={index * 0.05} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ LAB SECTION ============ */}
      <section style={{ padding: '6rem 0', backgroundColor: '#ffffff' }}>
        <div className="container-main">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
            gap: '4rem',
            alignItems: 'center'
          }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              style={{ position: 'relative' }}
            >
              <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={getMediaUrl('about/laboratory-equipment.jpg')}
                  alt="Laboratory"
                  className="w-full aspect-[4/3] sm:aspect-[16/10] object-cover"
                />
              </div>

              {/* Floating badge - hidden on mobile */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="hidden sm:block absolute -bottom-4 sm:-bottom-5 -right-4 sm:-right-5 p-3 sm:p-6 rounded-lg sm:rounded-xl"
                style={{
                  backgroundColor: '#00a3cc',
                  boxShadow: '0 20px 40px rgba(0,163,204,0.3)',
                }}
              >
                <Microscope className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#00a3cc',
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}>
                {t('certifications.rdLabel')}
              </span>
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 2.5rem)',
                fontWeight: 800,
                color: '#0f172a',
                marginTop: '0.5rem',
                marginBottom: '1.5rem'
              }}>
                {t('certifications.labTitle')}
              </h2>
              <p style={{
                fontSize: '1.125rem',
                color: '#64748b',
                lineHeight: 1.7
              }}>
                {t('certifications.labText')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ CTA SECTION ============ */}
      <section
        style={{
          padding: '6rem 0',
          background: 'linear-gradient(135deg, #003b7a 0%, #001f42 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative */}
        <motion.div
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            border: '1px solid rgba(0,163,204,0.1)',
          }}
        />

        <div className="container-main" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}
          >
            <Award
              style={{
                width: '48px',
                height: '48px',
                color: '#00a3cc',
                margin: '0 auto 1.5rem',
              }}
            />
            <h2
              style={{
                fontSize: 'clamp(2rem, 5vw, 2.75rem)',
                fontWeight: 800,
                color: '#ffffff',
                marginBottom: '1.5rem',
              }}
            >
              {t('home.ctaTitle')}
            </h2>
            <p
              style={{
                fontSize: '1.25rem',
                color: 'rgba(255,255,255,0.7)',
                marginBottom: '2.5rem',
                lineHeight: 1.6,
              }}
            >
              {t('home.ctaDescription')}
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}>
              <LocalizedLink to="/contact">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="primary" size="lg">
                    {t('buttons.contactUs')}
                    <ChevronRight style={{ marginLeft: '0.5rem', width: '20px', height: '20px' }} />
                  </Button>
                </motion.div>
              </LocalizedLink>

              <LocalizedLink to="/cathodic-protection">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="outline"
                    size="lg"
                    style={{
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: '#ffffff',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                    }}
                  >
                    {t('buttons.exploreProducts')}
                  </Button>
                </motion.div>
              </LocalizedLink>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}
